// ---------------------------------------------------------------------------
// contactForm — vanilla fetch submit for the enquiry form on ContactPage,
// replacing the legacy jQuery + parsley ContactForm.coffee flow.
//
// Markup contract (see html/ContactPage.html):
//   <form data-contact-form action="/api/contact"> … </form>
//   sibling status nodes: [data-contact-status] with [data-state] children
//   ("sending" | "sent" | "exists" | "error").
//
// Behaviour: native HTML5 validation gates submit; on submit we POST the form
// fields as JSON to the form's `action`. 2xx → "sent"; 409 → "exists" (already
// registered); anything else / network failure → "error" (which carries a
// mailto fallback in the markup so the page degrades gracefully even when no
// real endpoint is wired). State is reflected via `data-state` on the form.
// ---------------------------------------------------------------------------

type FormState = 'idle' | 'sending' | 'sent' | 'exists' | 'error';

const setState = (form: HTMLFormElement, state: FormState): void => {
	form.dataset.state = state;
	const submit = form.querySelector<HTMLButtonElement>('button[type="submit"]');
	if (submit) submit.disabled = state === 'sending';
};

const initForm = (form: HTMLFormElement): void => {
	setState(form, 'idle');

	form.addEventListener('submit', (event) => {
		event.preventDefault();
		if (!form.checkValidity()) {
			form.reportValidity();
			return;
		}

		const endpoint = form.getAttribute('action') || '/api/contact';
		const payload: Record<string, string> = {};
		for (const [key, value] of new FormData(form).entries()) {
			payload[key] = typeof value === 'string' ? value : '';
		}

		setState(form, 'sending');

		fetch(endpoint, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
			body: JSON.stringify(payload),
		})
			.then((res) => {
				if (res.ok) {
					form.reset();
					setState(form, 'sent');
				} else if (res.status === 409) {
					setState(form, 'exists');
				} else {
					setState(form, 'error');
				}
			})
			.catch(() => setState(form, 'error'));
	});
};

export const initContactForm = (): void => {
	const forms = document.querySelectorAll<HTMLFormElement>('form[data-contact-form]');
	for (const form of forms) initForm(form);
};
